import { cloneElement, isValidElement, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { t, type MessageKey } from '../i18n/index.ts';
import { Icon, type IconName } from './Icon.tsx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  busy,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  icon?: IconName;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      aria-busy={busy || undefined}
      className={`btn btn-${variant} btn-${size} ${className}`}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 20} /> : null}
      {children ? <span className="btn-label">{children}</span> : null}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  variant = 'ghost',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; variant?: Variant }) {
  return (
    <button type="button" {...rest} className={`icon-btn btn-${variant} ${rest.className ?? ''}`} aria-label={label} title={label}>
      <Icon name={icon} />
    </button>
  );
}

export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral' | 'primary';

/** Status is always colour + icon + label (GOLDEN-RULE.md, rule 4). */
export function StatusBadge({ tone, icon, label, pulse }: { tone: Tone; icon: IconName; label: string; pulse?: boolean }) {
  return (
    <span className={`badge badge-${tone}${pulse ? ' badge-pulse' : ''}`}>
      <Icon name={icon} size={14} />
      <span>{label}</span>
    </span>
  );
}

export function Skeleton({ h = 16, w = '100%', r = 'var(--radius-sm)' }: { h?: number | string; w?: number | string; r?: string }) {
  return <span className="skeleton" style={{ height: h, width: w, borderRadius: r }} aria-hidden="true" />;
}

export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = 'md',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Kept in a ref so a new onClose on re-render never moves the focus back to the first field.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[data-autofocus], input, button:not(.dialog-close)');
    // Without scrolling: in a long dialog the first button can be far down, and the top is what to read first.
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog dialog-${size}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header className="dialog-head">
          <h2>{title}</h2>
          <IconButton icon="x" label={t('common.close')} onClick={onClose} className="dialog-close" />
        </header>
        <div className="dialog-body">{children}</div>
        {footer ? <footer className="dialog-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Confirm({
  title,
  body,
  confirm,
  danger,
  onConfirm,
  onClose,
  busy,
}: {
  title: string;
  body: ReactNode;
  confirm: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} busy={busy} data-autofocus>
            {confirm}
          </Button>
        </>
      }
    >
      <div className="confirm-body">{body}</div>
    </Dialog>
  );
}

export function EmptyState({ icon, title, body, action }: { icon: IconName; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={28} />
      </span>
      <p className="empty-title">{title}</p>
      {body ? <p className="empty-body">{body}</p> : null}
      {action}
    </div>
  );
}

/** A labelled input. The hint or error is linked with aria-describedby, so it is read after the label, not as part of it. */
export function Field({
  label,
  hint,
  error,
  ok,
  required,
  counter,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  /** A quiet confirmation that what was typed looks right, e.g. a complete tax number. */
  ok?: string | null;
  /** Marks the field as one that must be filled, with a star and for screen readers. */
  required?: boolean;
  /** Shown at the end of the message row, e.g. "23/300". */
  counter?: string;
  children: ReactNode;
}) {
  const id = useId();
  const msgId = `${id}-msg`;
  const msg = error || ok || hint;
  const child = isValidElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean; 'aria-required'?: boolean }>(children)
    ? cloneElement(children, {
        id: children.props.id ?? id,
        'aria-describedby': msg ? msgId : undefined,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })
    : children;
  const line = error ? (
    <span className="field-msg" id={msgId} role="alert">
      <Icon name="alert" size={14} /> {error}
    </span>
  ) : ok ? (
    <span className="field-ok" id={msgId}>
      <Icon name="checkCircle" size={14} /> {ok}
    </span>
  ) : hint ? (
    <span className="field-hint" id={msgId}>
      {hint}
    </span>
  ) : null;
  return (
    <div className={`field${error ? ' field-error' : ok ? ' field-valid' : ''}`}>
      <label className="field-label" htmlFor={isValidElement<{ id?: string }>(children) ? (children.props.id ?? id) : undefined}>
        {label}
        {required ? (
          <span className="field-req" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {child}
      {counter ? (
        <div className="field-foot">
          {line ?? <span />}
          <span className="field-counter" aria-hidden="true">
            {counter}
          </span>
        </div>
      ) : (
        line
      )}
    </div>
  );
}

/** Large numeric keypad for PINs and amounts on touch screens. */
export function Keypad({ onKey, extra }: { onKey: (k: string) => void; extra?: { key: string; label: string } }) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', extra?.key ?? '', '0', 'back'];
  return (
    <div className="keypad" role="group">
      {keys.map((k, i) =>
        k === '' ? (
          <span key={`gap-${i}`} />
        ) : (
          <button
            key={k}
            type="button"
            className="key"
            onClick={() => onKey(k)}
            aria-label={k === 'back' ? t('common.delete_digit') : k === extra?.key ? extra.label : k}
          >
            {k === 'back' ? <Icon name="delete" size={22} /> : k === extra?.key ? extra.label : k}
          </button>
        ),
      )}
    </div>
  );
}

/** On/off switch. The label says what "on" means, e.g. "On sale". */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={`switch${checked ? ' on' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <span className="switch-thumb" />
    </button>
  );
}

export const label = (key: MessageKey) => t(key);

/** The top of a page: title, one line of context and the page's main actions (the primary one last). */
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  /** A short line above the title, e.g. the module a record belongs to. */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  /** Tabs or filters that belong to the header. */
  children?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-head">
        <div className="page-title">
          {eyebrow ? <p className="page-eyebrow">{eyebrow}</p> : null}
          <h1>{title}</h1>
          {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </div>
      {children}
    </header>
  );
}

/** A surface for one subject, with an optional title row and actions. */
export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
  flush,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** No inner padding: for a table or a list that runs to the edges. */
  flush?: boolean;
}) {
  return (
    <section className={`card${flush ? ' card-flush' : ''} ${className}`}>
      {title || actions ? (
        <div className="card-head">
          <div>
            {title ? <h2 className="card-title">{title}</h2> : null}
            {subtitle ? <p className="card-subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/**
 * One business figure: label, value, change against the previous period and a small trend line.
 * `good` says which direction is good news (more revenue is, more expenses is not), so the change
 * is coloured by meaning, and always also carries an arrow and a sign.
 */
export function KpiCard({
  label,
  value,
  icon,
  change,
  compare,
  good = 'up',
  trend,
  to,
  loading,
}: {
  label: string;
  value: ReactNode;
  icon?: IconName;
  /** Percentage change, e.g. 12.5 for +12.5 %. */
  change?: number | null;
  /** What the change is measured against, e.g. "vs last month". */
  compare?: string;
  good?: 'up' | 'down';
  trend?: ReactNode;
  to?: string;
  loading?: boolean;
}) {
  const dir = change == null || !Number.isFinite(change) || Math.abs(change) < 0.05 ? 'flat' : change > 0 ? 'up' : 'down';
  const tone = dir === 'flat' ? 'flat' : dir === good ? 'good' : 'bad';
  const body = (
    <>
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        {icon ? (
          <span className="kpi-icon" aria-hidden="true">
            <Icon name={icon} size={18} />
          </span>
        ) : null}
      </div>
      {loading ? <Skeleton h={30} w="60%" /> : <div className="kpi-value">{value}</div>}
      <div className="kpi-foot">
        {change != null && Number.isFinite(change) ? (
          <span className={`kpi-change kpi-${tone}`}>
            <Icon name={dir === 'down' ? 'arrowDown' : dir === 'up' ? 'arrowUp' : 'minus'} size={14} />
            {change >= 1000 ? `×${Math.round(1 + change / 100)}` : `${change > 0 ? '+' : ''}${change.toFixed(1)}%`}
          </span>
        ) : null}
        {compare ? <span className="kpi-compare">{compare}</span> : null}
      </div>
      {trend ? <div className="kpi-trend">{trend}</div> : null}
    </>
  );
  return to ? (
    <a className="kpi kpi-link" href={to}>
      {body}
    </a>
  ) : (
    <div className="kpi">{body}</div>
  );
}

/** Tabs that switch a part of the page. Arrow keys move between them (WAI-ARIA tabs pattern). */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { key: K; label: string; icon?: IconName; count?: number }[];
  value: K;
  onChange: (k: K) => void;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const move = (dir: number) => {
    const i = tabs.findIndex((x) => x.key === value);
    const next = tabs[(i + dir + tabs.length) % tabs.length]!;
    onChange(next.key);
    ref.current?.querySelector<HTMLButtonElement>(`[data-key="${next.key}"]`)?.focus();
  };
  return (
    <div
      className="tabs"
      role="tablist"
      aria-label={label}
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') move(1);
        else if (e.key === 'ArrowLeft') move(-1);
      }}
    >
      {tabs.map((x) => (
        <button
          key={x.key}
          type="button"
          role="tab"
          data-key={x.key}
          aria-selected={x.key === value}
          tabIndex={x.key === value ? 0 : -1}
          className={`tab${x.key === value ? ' active' : ''}`}
          onClick={() => onChange(x.key)}
        >
          {x.icon ? <Icon name={x.icon} size={16} /> : null}
          <span>{x.label}</span>
          {x.count ? <span className="tab-count">{x.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** A button that opens a short list of actions; Escape or a click outside closes it. */
export function Menu({
  label,
  icon = 'dots',
  items,
  align = 'end',
  trigger,
}: {
  label: string;
  icon?: IconName;
  items: ({ label: string; icon?: IconName; onSelect: () => void; danger?: boolean; disabled?: boolean } | 'divider')[];
  align?: 'start' | 'end';
  /** A custom trigger's content; without it an icon button with `label` as its name. */
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    box.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const all = [...(box.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const i = all.indexOf(document.activeElement as HTMLElement);
    all[(i + (e.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length]?.focus();
  };
  return (
    <div className="pop" ref={box}>
      <button
        type="button"
        className={trigger ? 'pop-trigger' : 'icon-btn btn-ghost'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={trigger ? undefined : label}
        title={trigger ? undefined : label}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger ?? <Icon name={icon} />}
      </button>
      {open ? (
        <div className={`pop-menu pop-${align}`} role="menu" aria-label={label} onKeyDown={onKey}>
          {items.map((it, i) =>
            it === 'divider' ? (
              <hr key={i} className="pop-divider" />
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                className={`pop-item${it.danger ? ' danger' : ''}`}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
              >
                {it.icon ? <Icon name={it.icon} size={18} /> : null}
                <span>{it.label}</span>
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A table that scrolls sideways inside its card on a narrow screen instead of widening the page. While it is wider
 * than its card it is a named region in the tab order, so a keyboard can scroll it too (WCAG 2.1.1); when it fits
 * it is a plain box and costs no extra Tab press.
 */
export function TableWrap({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const name = label ?? t('dt.scroll');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Set on the element itself, in the same frame the table changes size, so a wide table is
    // never shown (or checked) for a moment without keyboard access to its scrolling.
    const check = () => {
      if (el.scrollWidth > el.clientWidth + 1) {
        el.setAttribute('role', 'region');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-label', name);
      } else {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-label');
      }
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    // The table inside can be swapped (loading, then rows); watch whichever one is there now.
    let inner = el.firstElementChild;
    if (inner) ro.observe(inner);
    const mo = new MutationObserver(() => {
      if (el.firstElementChild !== inner) {
        if (inner) ro.unobserve(inner);
        inner = el.firstElementChild;
        if (inner) ro.observe(inner);
      }
      check();
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [name]);
  return (
    <div ref={ref} className={className ? `table-wrap ${className}` : 'table-wrap'}>
      {children}
    </div>
  );
}
